import { useInfiniteQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { PaginatedResponse } from "@/shared/http";

import { MetricRecord, QueryMetricsRequest } from "../MetricRecord";

import { hashMetricsQueryKey, metricsQueryKeys } from "./metricsQueryKeys";

/**
 * Paginated raw query for stored metric values whose session time falls in a
 * range, following `next_token` on demand. Returns all fetched pages on
 * `data.pages`.
 */
export function useInfiniteQueryMetrics(
  request: QueryMetricsRequest | undefined,
  orgId: string | undefined,
) {
  const { metricsService } = useDomainServices();

  return useInfiniteQuery<PaginatedResponse<MetricRecord>, Error>({
    queryKey: metricsQueryKeys.queriesInfinite(request, orgId),
    queryKeyHashFn: hashMetricsQueryKey,
    queryFn: ({ signal, pageParam }) => {
      if (request === undefined) {
        return Promise.reject(new Error("Missing query request"));
      }
      return metricsService.queryMetrics(request, {
        abortSignal: signal,
        orgId,
        pageToken: pageParam as string | undefined,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_token ?? undefined,
    enabled: request !== undefined && orgId !== undefined,
    staleTime: 60_000,
  });
}
